import { z } from 'zod';
import { IsoTimestamp, UserId } from './common';

export const PairingCode = z.object({
  code: z.string().regex(/^\d{6}$/),
  userId: UserId,
  createdAt: IsoTimestamp,
  expiresAt: IsoTimestamp,
  consumedAt: IsoTimestamp.optional(),
  ttl: z.number().int(),
});

export type PairingCode = z.infer<typeof PairingCode>;

export const CreatePairingRequest = z.object({
  email: z.string().email().optional(),
  /**
   * When true, the server emails the pairing code + DMG install link
   * to the user's stored email (or the `email` override, if provided).
   * Defaults to false so surfaces that just need to display the code
   * on-screen (e.g. the mobile setup-desktop screen showing the 6-digit
   * code for the user to type into the desktop installer) don't
   * silently spam the user's inbox every time they open the screen.
   * The "Email me the install link" button is the explicit opt-in:
   * it calls the endpoint with `sendEmail: true`.
   */
  sendEmail: z.boolean().optional(),
});

export const CreatePairingResponse = z.object({
  code: z.string(),
  expiresAt: IsoTimestamp,
});

export const RedeemPairingRequest = z.object({
  code: z.string().regex(/^\d{6}$/),
  platform: z.enum(['macos', 'windows', 'linux']),
  appVersion: z.string().optional(),
});

export const RedeemPairingResponse = z.object({
  // Short-lived bearer token sent on every API call.
  accessToken: z.string(),
  /** Long-lived token the desktop redeems for a new access token via
   *  /v1/desktop/refresh. Stored alongside `accessToken` in OS keychain. */
  refreshToken: z.string(),
  /** Seconds until accessToken expires. Client should refresh ~30s prior. */
  expiresIn: z.number().int().positive(),
  deviceId: z.string(),
  userId: z.string(),
  /** Whether this newly-paired device is the user's ACTIVE worker. False
   *  when the user already has another active device — the desktop UI then
   *  shows "Standby — switch from your phone to make this device active". */
  isActive: z.boolean().default(true),
});

export const RefreshTokenRequest = z.object({
  refreshToken: z.string(),
});

export const RefreshTokenResponse = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int().positive(),
});
